import { useEffect, useMemo, useRef } from 'react';

import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';

import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';
import {
	ANALYTICS_SOURCE,
	AT_A_GLANCE_ACTION_SUBJECT_ID,
	AT_A_GLANCE_SUMMARY_START_TRIGGER,
} from '../analytics.ts';

import {
	createAtAGlanceObservation,
	type AtAGlanceSummaryStartTrigger,
	type AtAGlanceCancellationReason,
	type AtAGlanceObservation,
} from './atAGlanceObservation.ts';

const pathname = (href: string | undefined): string | undefined => {
	if (!href) {
		return undefined;
	}
	try {
		return new URL(href, 'https://confluence.invalid').pathname;
	} catch {
		return undefined;
	}
};

export const useAtAGlanceObservation = (contentId: string): AtAGlanceObservation => {
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	let summaryStartTrigger: AtAGlanceSummaryStartTrigger | undefined;
	if (!isEmbeddedConfluence) {
		summaryStartTrigger = AT_A_GLANCE_SUMMARY_START_TRIGGER;
	}
	const { createAnalyticsEvent: createEvent } = useTwgAnalyticsEvents();
	const route = useRouteDataRef();
	const latest = useRef({ contentId, createEvent, summaryStartTrigger, mounted: true });
	latest.current = { ...latest.current, contentId, createEvent, summaryStartTrigger };
	const observation = useMemo(() => {
		let initialPath: string | undefined;
		let ownerSummaryStartTrigger = latest.current.summaryStartTrigger;
		return createAtAGlanceObservation({
			getSummaryStartTrigger: () => {
				if (latest.current.contentId === contentId) {
					ownerSummaryStartTrigger = latest.current.summaryStartTrigger;
				}
				return ownerSummaryStartTrigger;
			},
			emit: (attributes) => {
				initialPath ??= pathname(route.current?.href);
				latest.current
					.createEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'observed',
							actionSubject: 'atAGlanceBrowser',
							actionSubjectId: AT_A_GLANCE_ACTION_SUBJECT_ID,
							source: ANALYTICS_SOURCE,
							attributes,
						},
					})
					.fire();
			},
			getVisitEndReason: (): AtAGlanceCancellationReason => {
				try {
					const currentPath = pathname(route.current?.href);
					if (
						latest.current.contentId !== contentId ||
						(initialPath !== undefined && currentPath !== undefined && initialPath !== currentPath)
					) {
						return 'navigation';
					}
					if (latest.current.mounted) {
						return 'unknown';
					}
					return 'componentRemoval';
				} catch {
					return 'unknown';
				}
			},
		});
	}, [contentId, route]);
	useEffect(() => {
		latest.current.mounted = true;
		return () => {
			latest.current.mounted = false;
			// Defer the visit end so StrictMode effect replay does not split one visit.
			void Promise.resolve().then(() => {
				if (!latest.current.mounted || latest.current.contentId !== contentId) {
					observation.endVisit();
				}
			});
		};
	}, [observation, contentId]);
	return observation;
};
